//-------------------------------------------------------------------------------------------------- Imports
import { Button, Card, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StyleSheet } from "../../StyleSheet";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import InfoCardComponent from "../../components/InfoCardComponent";
import Loading from "../../components/Loading";
import { AppDispatch } from "../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import { getInternetSeguroAccount } from "../../stores/slices/internetSeguroSlice";
import {
  createInternetSeguroUser,
  updateInternetSeguroUser,
} from "../../methods/Methods";
import {
  InternetSeguroUser,
  RequestInternetSeguroCreateUser,
  ResponseInternetSeguroAccountDetail,
} from "../../types/common";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const ParentalControlAddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userId } = useParams();
  const isEditMode = !!userId;

  const account = useSelector(
    (state: any) =>
      state.internetSeguro
        .account as ResponseInternetSeguroAccountDetail | null,
  );
  const accountLoaded = useSelector(
    (state: any) => state.internetSeguro.accountLoaded,
  );
  const editingUser: InternetSeguroUser | undefined = (
    account?.users ?? []
  ).find((u) => u.userId === userId);

  const [alias, setAlias] = useState("");
  const [email, setEmail] = useState("");
  const [errorsData, setErrorsData] = useState<{
    emailAddress: string;
    alias: string;
  }>({
    emailAddress: "",
    alias: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && !accountLoaded) {
      dispatch(getInternetSeguroAccount());
    }
  }, [dispatch, isEditMode, accountLoaded]);

  useEffect(() => {
    if (isEditMode && editingUser) {
      setAlias(editingUser.username);
      setEmail(editingUser.email);
    }
  }, [isEditMode, editingUser]);

  const emailRegex = RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  const handleChangeEmail = (value: string) => {
    setErrorsData((prev) => ({ ...prev, emailAddress: "" }));
    setEmail(value);
  };

  const handleChangeAlias = (value: string) => {
    setErrorsData((prev) => ({ ...prev, alias: "" }));
    setAlias(value);
  };

  const openAlert = (
    type: "SUCCESS" | "ERROR",
    title: string,
    message: string,
    navigateBack: boolean,
  ) => {
    dispatch(
      setAlert({
        show: true,
        type,
        title,
        message,
        onFinalize: () => {
          dispatch(showAlert(false));
          dispatch(clearAlert());
          if (navigateBack) {
            navigate("/parental-control/users");
          }
        },
      }),
    );
  };

  const handleSubmit = () => {
    let aliasError = "";
    let emailError = "";
    if (!alias.trim()) {
      aliasError = "El alias es obligatorio.";
    }
    if (!email.trim()) {
      emailError = "El correo electrónico es obligatorio.";
    } else if (!emailRegex.test(email)) {
      emailError = "El formato del email es incorrecto.";
    }
    setErrorsData({ emailAddress: emailError, alias: aliasError });
    if (aliasError || emailError) {
      return;
    }

    setSubmitting(true);
    const body: RequestInternetSeguroCreateUser = {
      email: email.trim(),
      username: alias.trim(),
    };
    const request = isEditMode
      ? updateInternetSeguroUser(userId as string, body)
      : createInternetSeguroUser(body);

    request
      .then((response) => {
        setSubmitting(false);
        if (response && response.data && response.data.status === "SUCCESS") {
          dispatch(getInternetSeguroAccount());
          openAlert(
            "SUCCESS",
            isEditMode ? "Cambios guardados" : "Invitación enviada",
            isEditMode
              ? "Se ha actualizado con éxito los datos del usuario."
              : "Se ha enviado la invitación con éxito.",
            true,
          );
        } else {
          openAlert(
            "ERROR",
            "¡Estimado usuario!",
            "Hubo un error al procesar su solicitud. Intente más tarde.",
            false,
          );
        }
      })
      .catch(() => {
        setSubmitting(false);
        openAlert(
          "ERROR",
          "¡Estimado usuario!",
          "Hubo un error al procesar su solicitud. Intente más tarde.",
          false,
        );
      });
  };

  if (isEditMode && !accountLoaded) {
    return <Loading />;
  }

  return (
    <SubDefaultLayout
      title={isEditMode ? "Editar usuario" : "Agregar usuario"}
      needReturn={true}
      navigateTo={
        isEditMode
          ? `/parental-control/users/${userId}`
          : "/parental-control/users"
      }
    >
      <div style={styles.contentContainer}>
        {!isEditMode && (
          <InfoCardComponent
            color="#c5d5e4"
            title="Invitación"
            description="Ingresa el correo electrónico de la persona que utilizará Internet Seguro. Le enviaremos una invitación para activar la protección."
          />
        )}
        <Text style={styles.descriptionText}>
          Ingresa un correo electrónico para asociarlo a tus usuarios en Mega
          Control Parental
        </Text>
        <Card style={{ ...styles.cardRoot }}>
          <Text style={styles.inputTitle}>Alias</Text>
          <Input
            value={alias}
            placeholder="Alias*"
            status={errorsData.alias ? "error" : undefined}
            onChange={(e) => handleChangeAlias(e.target.value)}
            style={styles.inputText}
          />
          {errorsData.alias && (
            <Text type="danger" style={styles.emptyFieldError}>
              {errorsData.alias}
            </Text>
          )}
          <Text style={styles.inputTitle}>Correo electrónico</Text>
          <Input
            value={email}
            placeholder="Correo electrónico*"
            status={errorsData.emailAddress ? "error" : undefined}
            onChange={(e) => handleChangeEmail(e.target.value)}
            style={styles.inputText}
          />
          {errorsData.emailAddress && (
            <Text type="danger" style={styles.emptyFieldError}>
              {errorsData.emailAddress}
            </Text>
          )}
          <div style={styles.buttonContainer}>
            <Button
              size="large"
              style={styles.buttonPrimary}
              onClick={handleSubmit}
              loading={submitting}
            >
              {isEditMode ? "Guardar cambios" : "Enviar invitación"}
            </Button>
          </div>
        </Card>
      </div>
    </SubDefaultLayout>
  );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
  contentContainer: {
    width: "70%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  cardRoot: {
    backgroundColor: "white",
    marginBottom: 15,
    boxShadow: "0px 0px 8px #00000029",
    borderRadius: 20,
    width: "70%",
  },
  descriptionText: {
    marginTop: 25,
    marginBottom: 20,
    textAlign: "center",
    fontSize: 15,
  },
  inputContainer: {
    width: "100%",
    maxWidth: 450,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  inputTitle: {
    color: "#0055c2",
    fontWeight: 600,
    fontSize: 12,
  },
  inputText: {
    marginTop: 15,
    color: "#000000",
    borderColor: "#000000",
    fontSize: 12,
    marginBottom: 10,
  },
  emptyFieldError: {
    fontSize: 11,
    display: "block",
  },
  errorText: {
    color: "#ff4d4f",
    fontSize: 12,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: 25,
  },
  buttonPrimary: {
    backgroundColor: "#0055c2",
    borderRadius: 30,
    color: "white",
    border: "none",
    padding: "5px 40px",
  },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ParentalControlAddUser;
